import { HeaderButton } from 'components/common';
import { ChatIcon } from 'components/common/Icons';
import {
  DialogNotifySettings,
  ExportCsv,
  FilterInput,
  ViewButton,
  ViewToggle,
} from 'components/core';
import { useContext, useState } from 'react';
import {
  ColumnContext,
  FilterContext,
  ListContext,
  ViewContext,
} from 'utils/context';
import DK from 'utils/dictionaries/filter_dictionary';
import { usePermissions } from 'utils/hooks';

import {
  Flex,
  Icon,
  IconButton,
  reactIcons as Icons,
  Separator,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

interface HeaderProps {
  doSearch: () => void;
  isChatOpen?: boolean;
  isColumnOpen?: boolean;
  isFilterOpen?: boolean;
  onClickChat?: () => void;
  onClickColumn?: () => void;
  onClickFilter?: () => void;
  totalOrders?: number;
  totalShipments?: number;
}

function Header({
  doSearch,
  isChatOpen,
  isColumnOpen = false,
  isFilterOpen = false,
  onClickChat,
  onClickColumn,
  onClickFilter,
  totalOrders,
  totalShipments,
}: HeaderProps) {
  const {
    flags: { featureExc2241, featureExc2157 },
    permissions: { canViewChat },
  } = usePermissions();
  const { t } = useTranslation();
  const { getFilterCount } = useContext(FilterContext);
  const { shipmentsData } = useContext(ListContext);
  const { selectedView, viewType } = useContext(ViewContext);
  const { hasColumnsModified } = useContext(ColumnContext);
  const filterCount = getFilterCount();
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);

  const allowNotify =
    featureExc2157 &&
    selectedView?.id !== 'all-shipments' &&
    viewType === 'shipment';

  const EXPORT_RECORDS_LIMIT = 10_000;
  const pageCount = shipmentsData.paginationInfo?.total || 0;
  const placeholder = t('core.searchEntity', {
    entity: t(viewType === 'order' ? 'core.orders' : 'core.shipments'),
  });

  return (
    <Flex
      data-testid="page-layout-header"
      align="center"
      css={{ flex: 1, padding: '$medium' }}
      justify="between"
    >
      <Flex gap="small" align="center">
        <ViewToggle totalOrders={totalOrders} totalShipments={totalShipments} />
        <Separator
          orientation="vertical"
          css={{
            backgroundColor: '$palette-indigo-200',
            height: '40px',
            alignSelf: 'center',
          }}
        />
        {allowNotify && (
          <IconButton
            onClick={() => {
              setIsNotifyOpen((prev) => !prev);
            }}
            children={<Icon icon="notifications" />}
            css={{
              backgroundColor: isNotifyOpen
                ? '$primary-default'
                : '$primary-active',
              border: ' 1px solid $border-primary',
              '&:hover': { backgroundColor: '$primary-hover' },
            }}
          />
        )}
        <ViewButton doSearch={doSearch} />
      </Flex>
      <Flex gap="medium">
        <FilterInput
          id={DK.SEARCH_TEXT}
          doSearch={doSearch}
          placeholder={placeholder}
        />
        {canViewChat && (
          <HeaderButton
            data-testid="header-chat-button"
            children={t('core.chat.movementGpt')}
            isActive={isChatOpen}
            onPress={onClickChat}
            startIcon={<ChatIcon size="24px" fill="white" />}
          />
        )}
        <HeaderButton
          data-testid="header-filter-button"
          children={t('global.filter')}
          count={filterCount}
          isActive={isFilterOpen}
          onPress={onClickFilter}
          startIcon={<Icons.Filter />}
        />
        <HeaderButton
          data-testid="header-column-button"
          children={t('core.column')}
          count={hasColumnsModified}
          isActive={isColumnOpen}
          onPress={onClickColumn}
          startIcon={<Icon icon="view_week" style={{ width: '24px' }} />}
        />
        {featureExc2241 && viewType === 'shipment' && (
          <ExportCsv showLimitWarning={pageCount > EXPORT_RECORDS_LIMIT} />
        )}
      </Flex>
      <DialogNotifySettings
        isOpen={isNotifyOpen}
        onClose={() => setIsNotifyOpen(false)}
      />
    </Flex>
  );
}

export default Header;
